// src/app/pages/events/events.data.ts
//
// ─── HOW TO ADD OR REMOVE EVENTS ────────────────────────────────────────────
//
//  ADD:    Copy one of the objects below, paste it into the array, fill in fields.
//  REMOVE: Delete the object from the array.
//
//  REQUIRED fields: title, dateTime, time, location, city, description, type
//
//  dateTime format: 'YYYY-MM-DDTHH:MM'  (24-hour, local time)
//    Examples: '2026-06-04T18:00' → June 4 2026 at 6:00 PM
//              '2026-03-28T14:00' → March 28 2026 at 2:00 PM
//
//  For RECURRING events (e.g. every Sunday): set recurring: true
//    These always appear in "Upcoming" regardless of dateTime.
//    Set dateTime to the next scheduled occurrence for display purposes.
//
//  type options:
//    'signature-drive' | 'town-hall' | 'canvass' | 'phone-bank' | 'rally' | 'other'
//
// ────────────────────────────────────────────────────────────────────────────

export interface CampaignEvent {
  title: string;
  dateTime: string;       // ISO local: 'YYYY-MM-DDTHH:MM' — used for sorting & past/upcoming logic
  displayDate: string;    // Human-readable date
  time: string;           // Display time
  location: string;
  city: string;
  description: string;
  type:
    | 'signature-drive'
    | 'town-hall'
    | 'canvass'
    | 'phone-bank'
    | 'rally'
    | 'other';
  rsvpUrl?: string;
  recurring?: boolean;
  virtual?: boolean;
  partnerName?: string;
  partnerUrl?: string;
}

export const CAMPAIGN_EVENTS: CampaignEvent[] = [

  // ─── PAST EVENTS ──────────────────────────────────────────────────────────

  {
    title: 'May Day Voter Registration Drive',
    dateTime: '2026-05-01T10:00',
    displayDate: 'Friday, May 1, 2026',
    time: '10:00 AM – 6:00 PM',
    location: 'Statewide — Your Community',
    city: '',
    description:
      'Join Lydia and volunteers across Michigan for a May Day Voter Registration Drive. ' +
      'Help register new voters and collect petition signatures for the ballot. ' +
      'Sign up to host or join a registration table in your area.',
    type: 'other',
    rsvpUrl: '/#/may-day',
  },

  {
    title: 'Michigan Supreme Court Public Hearing',
    dateTime: '2026-05-20T13:00',
    displayDate: 'Wednesday, May 20, 2026',
    time: 'Public Hearing',
    location: 'Michigan Supreme Court',
    city: 'Lansing, MI',
    description:
      'Lydia Christensen spoke before the Michigan Supreme Court during a public hearing concerning proposed amendments to the Michigan Court Rules.',
    type: 'other',
  },

  {
    title: 'Signature Collection – U.P. Beat Music Festival (Day 1)',
    dateTime: '2026-05-23T11:00',
    displayDate: 'Saturday, May 23, 2026',
    time: '11:00 AM – 6:00 PM',
    location: 'Downtown Ironwood',
    city: 'Ironwood, MI 49938',
    description:
      'Stop by our table at the U.P. Beat Music Festival and sign Lydia\'s ballot petition! ' +
      'We need 15,000 signatures from registered Michigan voters by July 16 at 4:00 PM to get on the ballot. ' +
      'Enjoy live music and help put an independent voice on the ballot — every signature counts.',
    type: 'signature-drive',
  },

  {
    title: 'Signature Collection – U.P. Beat Music Festival (Day 2)',
    dateTime: '2026-05-24T11:00',
    displayDate: 'Sunday, May 24, 2026',
    time: '11:00 AM – 6:00 PM',
    location: 'Downtown Ironwood',
    city: 'Ironwood, MI 49938',
    description:
      'Last day of the U.P. Beat Music Festival — sign Lydia\'s ballot petition while you enjoy the music! ' +
      'We need 15,000 signatures from registered Michigan voters by July 16 at 4:00 PM to get on the ballot. ' +
      'Bring friends and neighbors — every signature gets us one step closer.',
    type: 'signature-drive',
  },

  {
    title: 'Memorial Day – Honoring Our Veterans',
    dateTime: '2026-05-25T09:00',
    displayDate: 'Monday, May 25, 2026',
    time: '9:00 AM',
    location: 'Local Veterans Cemetery',
    city: 'Bessemer, MI',
    description:
      'Lydia will be honoring the men and women who gave everything for our country ' +
      'by placing flags on the headstones of fallen veterans this Memorial Day. ' +
      'No hero forgotten.',
    type: 'other',
  },

  {
    title: 'Lights of Pride 2026',
    dateTime: '2026-06-04T18:00',
    displayDate: 'Thursday, June 4, 2026',
    time: 'Evening - details TBD',
    location: 'Location to be determined',
    city: '',
    description:
      'A night of music, stories, and community celebrating Pride with Lydia Christensen and the Iron Range GSA. ' +
      'Details on venue and time will be announced soon - check back for updates.',
    type: 'rally',
    partnerName: 'Iron Range GSA',
    partnerUrl: 'https://www.facebook.com/IronRangeGSA',
  },

  {
    title: 'Libertarian Party of Michigan Nominating Convention',
    dateTime: '2026-06-28T10:00',
    displayDate: 'Sunday, June 28, 2026',
    time: 'All Day',
    location: 'Troy, Michigan',
    city: '',
    description:
      'Lydia Christensen was officially nominated as the Libertarian Party candidate for U.S. Senate in Michigan at the state party\'s nominating convention in Troy.',
    type: 'other',
  },

  {
    title: 'Podcast – Libertarian Party Alliance',
    dateTime: '2026-07-21T21:00',
    displayDate: 'Tuesday, July 21, 2026',
    time: '8:00 PM Central / 9:00 PM Eastern',
    location: 'YouTube Live',
    city: '',
    description:
      'Lydia joins the Libertarian Party Alliance podcast for a live conversation. Tune in on YouTube.',
    type: 'other',
    rsvpUrl: 'https://www.youtube.com/watch?v=kgRYoN_n_e8',
    virtual: true,
  },

  {
    title: 'Podcast – Long Live Alternative Parties',
    dateTime: '2026-07-23T13:15',
    displayDate: 'Thursday, July 23, 2026',
    time: '12:15 PM Central / 1:15 PM Eastern',
    location: 'Online',
    city: '',
    description:
      'Lydia joins the Long Live Alternative Parties podcast for a live conversation.',
    type: 'other',
    virtual: true,
  },

  {
    title: 'Tabling – Ironwood Festival',
    dateTime: '2026-07-18T11:00',
    displayDate: 'Saturday, July 18, 2026',
    time: '11:00 AM – 6:00 PM',
    location: 'Depot Park',
    city: 'Ironwood, MI 49938',
    description:
      'Lydia and volunteers tabled at the Ironwood Festival in downtown Depot Park, talking with voters and sharing information about the campaign.',
    type: 'other',
  },

  {
    title: 'Kent County Youth Fair',
    dateTime: '2026-08-03T10:00',
    displayDate: 'Monday, August 3 – Saturday, August 8, 2026',
    time: 'Fair runs Aug 3–8 - Lydia\'s exact day(s) TBD',
    location: 'Grand Agricultural Center of West Michigan',
    city: 'Lowell, MI',
    description:
      'Lydia was invited to the Kent County Youth Fair.',
    type: 'other',
  },

  {
    title: 'Meet & Greet – Cheboygan County Fair & Carnival',
    dateTime: '2026-08-11T10:00',
    displayDate: 'Tuesday, August 11 – Saturday, August 15, 2026',
    time: 'Fair runs Aug 11–15 - Lydia\'s exact day(s) TBD',
    location: 'Cheboygan County Fairgrounds',
    city: 'Cheboygan, MI',
    description:
      'Lydia plans to stop by the Cheboygan County Fair & Carnival for a meet and greet with voters.',
    type: 'other',
  },

  {
    title: 'Animikiiwadiswan (Thunderbird Nest) Tribal Encampment',
    dateTime: '2026-08-14T15:00',
    displayDate: 'Friday, August 14 – Sunday, August 16, 2026',
    time: 'Begins Friday at 3:00 PM Central Time',
    location: 'Ottawa National Forest, west of the North Country Trail parking lot',
    city: 'County Road 519, about 20 minutes north of Wakefield, MI',
    description:
      'Join Tribal Nations and allies in respectful solidarity at Animikiiwadiswan (Thunderbird Nest), ' +
      'an encampment near the proposed Copperwood Mine. Bring your own food, water, lighting, dishware, and camp chair; ' +
      'carpool when possible. No drugs or alcohol; pack in and pack out.',
    type: 'other',
  },

  {
    title: 'Animikiiwadiswan (Thunderbird Nest) Tribal Encampment',
    dateTime: '2026-08-21T15:00',
    displayDate: 'Friday, August 21 – Sunday, August 23, 2026',
    time: 'Begins Friday at 3:00 PM Central Time',
    location: 'Ottawa National Forest, west of the North Country Trail parking lot',
    city: 'County Road 519, about 20 minutes north of Wakefield, MI',
    description:
      'Join Tribal Nations and allies in respectful solidarity at Animikiiwadiswan (Thunderbird Nest), ' +
      'an encampment near the proposed Copperwood Mine. Bring your own food, water, lighting, dishware, and camp chair; ' +
      'carpool when possible. No drugs or alcohol; pack in and pack out.',
    type: 'other',
  },

  {
    title: 'Mackinac Straits Corridor Authority Public Meeting',
    dateTime: '2026-08-28T10:00',
    displayDate: 'Friday, August 28, 2026',
    time: '10:00 AM – 1:00 PM',
    location: 'Little Bear East Arena and Community Center',
    city: 'St. Ignace, MI 49781',
    description:
      'Lydia attended the Mackinac Straits Corridor Authority public meeting in St. Ignace, ' +
      'which addressed updates concerning the proposed Line 5 utility tunnel beneath the Straits of Mackinac, ' +
      'including permitting, project documents, construction specifications, and the general project update.',
    type: 'other',
  },

  {
    title: 'Animikiiwadiswan (Thunderbird Nest) Tribal Encampment',
    dateTime: '2026-09-04T15:00',
    displayDate: 'Friday, September 4 – Sunday, September 6, 2026',
    time: 'Begins Friday at 3:00 PM Central Time',
    location: 'Ottawa National Forest, west of the North Country Trail parking lot',
    city: 'County Road 519, about 20 minutes north of Wakefield, MI',
    description:
      'Join Tribal Nations and allies in respectful solidarity at Animikiiwadiswan (Thunderbird Nest), ' +
      'an encampment near the proposed Copperwood Mine. Bring your own food, water, lighting, dishware, and camp chair; ' +
      'carpool when possible. No drugs or alcohol; pack in and pack out.',
    type: 'other',
  },

  {
    title: 'Animikiiwadiswan (Thunderbird Nest) Tribal Encampment',
    dateTime: '2026-09-11T15:00',
    displayDate: 'Friday, September 11 – Sunday, September 13, 2026',
    time: 'Begins Friday at 3:00 PM Central Time',
    location: 'Ottawa National Forest, west of the North Country Trail parking lot',
    city: 'County Road 519, about 20 minutes north of Wakefield, MI',
    description:
      'Join Tribal Nations and allies in respectful solidarity at Animikiiwadiswan (Thunderbird Nest), ' +
      'an encampment near the proposed Copperwood Mine. Bring your own food, water, lighting, dishware, and camp chair; ' +
      'carpool when possible. No drugs or alcohol; pack in and pack out.',
    type: 'other',
  },

  {
    title: 'Animikiiwadiswan (Thunderbird Nest) Tribal Encampment',
    dateTime: '2026-09-18T15:00',
    displayDate: 'Friday, September 18 – Sunday, September 20, 2026',
    time: 'Begins Friday at 3:00 PM Central Time',
    location: 'Ottawa National Forest, west of the North Country Trail parking lot',
    city: 'County Road 519, about 20 minutes north of Wakefield, MI',
    description:
      'Join Tribal Nations and allies in respectful solidarity at Animikiiwadiswan (Thunderbird Nest), ' +
      'an encampment near the proposed Copperwood Mine. Bring your own food, water, lighting, dishware, and camp chair; ' +
      'carpool when possible. No drugs or alcohol; pack in and pack out.',
    type: 'other',
  },

  {
    title: 'Podcast – Libertarian Leadership Academy',
    dateTime: '2026-09-10T21:00',
    displayDate: 'Thursday, September 10, 2026',
    time: '9:00 PM Eastern Time',
    location: 'Online',
    city: '',
    description:
      'Lydia Christensen joined the Libertarian Leadership Academy for a podcast conversation ' +
      'about her campaign and candidacy for U.S. Senate.',
    type: 'other',
    virtual: true,
  },

  // ─── RECURRING EVENTS ─────────────────────────────────────────────────────

  {
    title: 'Weekly Organizing Meeting',
    dateTime: '2026-04-05T11:00',
    displayDate: 'Every Sunday',
    time: '11:00 AM',
    location: 'Virtual',
    city: '',
    description:
      'Our weekly campaign organizing call - open to all volunteers and supporters. ' +
      'Coordinate canvassing, signature drives, outreach, and more. ' +
      'Join from anywhere in Michigan.',
    type: 'other',
    recurring: true,
    virtual: true,
  },

  // ─── UPCOMING EVENTS ──────────────────────────────────────────────────────

  {
    title: 'Multi Party Debate Participation Zoom',
    dateTime: '2026-09-20T20:00',
    displayDate: 'Sunday, September 20, 2026',
    time: '8:00 PM – 9:00 PM ET',
    location: 'Zoom',
    city: 'Virtual',
    description:
      'A multi-party discussion concerning access for minor-party candidates to televised debates ' +
      'and potential legal strategies to challenge exclusion from debate opportunities. ' +
      'Organized by Scott Aughney, U.S. Taxpayers Party candidate for Michigan Secretary of State.',
    type: 'other',
    virtual: true,
    rsvpUrl:
      'https://us06web.zoom.us/j/82216111492?pwd=b7xii7fLNZl2U44NFZl2U44HybCooEC.1',
  },

  {
    title: 'Wisconsin Natural Resources Board Meeting – Public Comment',
    dateTime: '2026-09-23T08:30',
    displayDate: 'Wednesday, September 23, 2026',
    time: '8:30 AM – Meeting Start',
    location: 'Stone Harbor Resort & Conference Center',
    city: 'Sturgeon Bay, WI 54235',
    description:
      'Lydia will speak during the Wisconsin Natural Resources Board meeting public comment period. ' +
      'The meeting begins at 8:30 AM Central Time. Public participation is available in person or virtually via Zoom.',
    type: 'other',
    virtual: true,
  },

  {
    title: '18th Annual Legislative Luncheon',
    dateTime: '2026-10-02T11:00',
    displayDate: 'Friday, October 2, 2026',
    time: '11:00 AM – 1:00 PM',
    location: 'Zellers Village Inn',
    city: 'Newberry, MI',
    description:
      'Join Grow, Save, Restore Newberry and the Luce County EDC for the 18th Annual Legislative Luncheon. ' +
      'Sponsorships are available.',
    type: 'other',
    rsvpUrl: 'mailto:lucecountyedc@gmail.com',
    partnerName: 'Grow, Save, Restore Newberry and Luce County EDC',
  },

  {
    title: 'Candidate Interview – Libertydad',
    dateTime: '2026-10-07T20:00',
    displayDate: 'Wednesday, October 7, 2026',
    time: '8:00 PM – 9:00 PM Eastern Time',
    location: 'Online',
    city: '',
    description:
      'Lydia joins Libertydad for a candidate interview and conversation.',
    type: 'other',
    virtual: true,
  },

  {
    title: 'Urban Neighborhood Initiatives Annual Trunk or Treat',
    dateTime: '2026-10-17T12:00',
    displayDate: 'Saturday, October 17, 2026',
    time: '12:00 PM – 3:00 PM',
    location: 'Springdale Green Playlot',
    city: 'Detroit, MI 48209',
    description:
      'Lydia was invited to host an outreach table at Urban Neighborhood Initiatives’ Annual Trunk or Treat, ' +
      'an annual neighborhood Halloween event where more than 300 families attend. ' +
      'Campaign attendance is not yet confirmed.',
    type: 'other',
    partnerName: 'Urban Neighborhood Initiatives',
    partnerUrl: 'https://www.unidetroit.org/',
  },
];